import useApi, {useApollo} from "/@src/composable/useApi";
import {ref} from "vue";
import {ContentKind, ContentListRequestParam, ListResponse} from "/@src/models/api";
import {ContentLevel, MediaFile} from "/@src/models/content";
import {deleteMedia, editMedia, fetchFileMeta, getMediaList} from "/@src/api/mediaRepo";
import {useI18n} from "vue-i18n";


export default function useMediaFiles() {
  const api = useApi()
  const graph = useApollo()
  const {t} = useI18n()
  const filesResponse = ref<ListResponse<MediaFile>>()
  const getReposts = async (params:ContentListRequestParam)=>{
    const res = await getMediaList(api,params,true)
    if(res.list&&res.list.length>0){
      res.list.forEach(it=>{
        it.kind = ContentKind.REPORT
        if(it.vip_visibility === 0){
          it.vip_visibility = ContentLevel.NORMAL
        }
        it.logo = getFileLogo(it)
      })
    }
    filesResponse.value = res
  }
  const mapperFileMimeLogo = (mime:string,fileUrl:string):string=>{
    let logo = ''
    if (mime.indexOf('image/') >= 0) {
      logo = fileUrl
    } else if (mime.indexOf('video/') >= 0) {
      logo = '/images/icons/files/video.svg'
    } else if (mime.indexOf('application/pdf') >= 0) {
      logo = '/images/icons/files/pdf.svg'
    } else if(mime.indexOf('msword') >= 0 || mime.indexOf('wordprocessingml.document') >= 0) {
      logo = '/images/icons/files/doc-2.svg'
    } else if(mime.indexOf('spreadsheetml.sheet') >= 0||mime.indexOf('ms-excel') >= 0) {
      logo = '/images/icons/files/sheet.svg'
    }else if(mime.indexOf('powerpoint') >= 0||mime.indexOf('presentationml.presentation')>0){
      logo = '/images/icons/files/ppt.svg'
    }else {
      logo = 'https://dummyimage.com/400x400/fff/000&text=' + mime
    }
    return logo
  }
  const getFileLogo = (item:MediaFile):string=>{
    const mime = item.file_meta!!.mime!! || ''
    return mapperFileMimeLogo(mime,item.file_uri)
  }
  const getFileLogoByExtension = (extension:string,defaultSrc?:string):string=>{
    let src = ''
    switch (extension.toLowerCase().replace('.','')) {
      case 'jpg':
      case 'jpeg':
      case 'png':
        src = '/images/img-loading-default.png'
        break
      case 'pdf':
        src = '/images/icons/files/pdf.svg'
        break
      case 'mp4':
      case 'avi':
      case 'flv':
      case 'mkv':
        src = '/images/icons/files/video.svg'
        break
      case 'doc':
      case 'docx':
        src = '/images/icons/files/doc-2.svg'
        break
      case 'ppt':
      case 'pptx':
        src = '/images/icons/files/ppt.svg'
        break
      case 'xls':
      case 'xlsx':
      case 'csv':
        src = '/images/icons/files/sheet.svg'
        break
      default:
        if(defaultSrc){
          src = defaultSrc
        }else {
          src = '/images/img-loading-default.png'
        }
        break
    }
    return src
  }

  const removeFile = (id:number)=>{
    return deleteMedia(api,id).then(()=>{
      if(filesResponse.value&&filesResponse.value?.list.length>0){
        let find = -1
        for(let index=0;index<filesResponse.value?.list.length;index++){
          if(filesResponse.value?.list[index].id === id){
            find = index
            break
          }
        }
        if(find>=0){
          filesResponse.value?.list.splice(find,1)
        }
      }
    })
  }
  const updateMediaFile = (file:MediaFile)=>{
    return editMedia(api,file)
  }
  const updateMediaFiles = async (files:Array<MediaFile>)=>{
    await Promise.all(files.map(it => editMedia(api, it)))
  }
  const getFileSize = (file:MediaFile)=>{
    let size = ''
    const meta = file.file_meta
    if(!meta){
      return size
    }
    if(meta.fsize<1024){
      size = meta.fsize + 'byte'
    }else if(meta.fsize<1024*1024){
      size = Math.round(meta.fsize/1024) + 'KB'
    }else {
      size = Math.round(meta.fsize/1024/1024*10)/10 + 'MB'
    }
    return size
  }
  const getFileType = (mime:string):string=>{
    let type = ''
    if (mime.indexOf('image/') >= 0) {
      type = t('file-type-image')
    } else if (mime.indexOf('video/') >= 0) {
      type = t('file-type-video')
    } else if (mime.indexOf('application/pdf') >= 0) {
      type = t('file-type-pdf')
    } else if(mime.indexOf('msword') >= 0 || mime.indexOf('wordprocessingml.document') >= 0) {
      type = t('file-type-doc')
    } else if(mime.indexOf('spreadsheetml.sheet') >= 0||mime.indexOf('ms-excel') >= 0) {
      type = t('file-type-excel')
    }else if(mime.indexOf('powerpoint') >= 0||mime.indexOf('presentationml.presentation')>0){
      type = t('file-type-ppt')
    }else {
      type = t('file-type-other')
    }
    return type
  }
  const getFileMeta = (key:string)=>{
    return fetchFileMeta(api,key).then((res)=>{
      res.mime = res.mimeType
      return res
    })
  }
  return {
    getFileLogo,
    mapperFileMimeLogo,
    getFileLogoByExtension,
    getReposts,
    getFileSize,
    getFileType,
    removeFile,
    getFileMeta,
    updateMediaFile,
    updateMediaFiles,
    filesResponse
  }
}
